// iOS hardware silent (ringer) switch + Web Audio.
//
// iOS routes an AudioContext through the "ambient" audio session, which the
// hardware silent switch mutes — so the member player goes quiet when the phone
// is on silent, even though other apps still play. Playing an HTMLMediaElement
// flips iOS to the "playback" session (ignores the switch). We keep a tiny,
// silent, looping <audio> alive, started on the first user gesture. Mirrors the
// well-known swevans/unmute technique. No-op off iOS.

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iP(hone|ad|od)/.test(ua)) return true;
  // iPadOS 13+ reports as "Macintosh"; disambiguate by touch support.
  return ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document;
}

/** Build a ~0.3s silent 8-bit mono WAV at runtime and return an object URL.
 *  Generated in code (no embedded base64). 0x80 is silence for 8-bit PCM. */
function makeSilentWavUrl(): string {
  const rate = 8000;
  const samples = Math.floor(rate * 0.3);
  const bytes = new Uint8Array(44 + samples);
  const dv = new DataView(bytes.buffer);
  const writeStr = (off: number, s: string): void => {
    for (let i = 0; i < s.length; i++) bytes[off + i] = s.charCodeAt(i);
  };
  writeStr(0, 'RIFF'); dv.setUint32(4, 36 + samples, true); writeStr(8, 'WAVE');
  writeStr(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
  dv.setUint32(24, rate, true); dv.setUint32(28, rate, true); dv.setUint16(32, 1, true); dv.setUint16(34, 8, true);
  writeStr(36, 'data'); dv.setUint32(40, samples, true);
  bytes.fill(128, 44);
  return URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
}

/**
 * Install a one-shot gesture listener that, on iOS, starts a silent looping
 * <audio> to switch the audio session to "playback" (so the silent switch no
 * longer mutes Web Audio). Returns a teardown that removes listeners + the tag.
 * No-op when not on iOS or when there is no DOM.
 */
export function enableIosSilentPlayback(): () => void {
  if (!isIos() || typeof document === 'undefined') return () => {};

  const events: Array<keyof DocumentEventMap> = ['touchend', 'mousedown', 'pointerdown', 'keydown'];
  let tag: HTMLAudioElement | null = null;
  let url: string | null = null;
  let armed = true;

  const onGesture = (): void => {
    if (!armed) return;
    armed = false;
    for (const e of events) document.removeEventListener(e, onGesture, true);
    try {
      url = makeSilentWavUrl();
      tag = document.createElement('audio');
      tag.setAttribute('x-webkit-airplay', 'deny');
      tag.setAttribute('playsinline', '');
      tag.loop = true;
      tag.preload = 'auto';
      tag.src = url;
      tag.style.display = 'none';
      document.body.appendChild(tag);
      tag.load();
      void tag.play().catch(() => { /* gesture too weak / unsupported — ignore */ });
    } catch { /* ignore */ }
  };

  for (const e of events) document.addEventListener(e, onGesture, { capture: true, passive: true });

  return (): void => {
    armed = false;
    for (const e of events) document.removeEventListener(e, onGesture, true);
    try { tag?.pause(); } catch { /* ignore */ }
    try { tag?.remove(); } catch { /* ignore */ }
    if (url) { try { URL.revokeObjectURL(url); } catch { /* ignore */ } }
    tag = null;
    url = null;
  };
}
